"use client";

import {
  Loader2,
  Maximize2,
  Minimize2,
  RotateCcw,
  Share2,
  X,
} from "lucide-react";

import SabineLead from "@/components/SabineLead";
import {
  NAV_ICON_BUTTON,
  NAV_INK_ICON_BUTTON,
} from "@/components/nav/nav-styles";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { SABINE_SUBTITLE, SABINE_TITLE } from "@/lib/sabine";
import { cn } from "@/lib/utils";

const HeaderButton = ({ label, ink, children, ...props }) => (
  <SimpleTooltip content={label} side="bottom">
    <button
      type="button"
      aria-label={label}
      className={ink ? NAV_INK_ICON_BUTTON : NAV_ICON_BUTTON}
      {...props}
    >
      {children}
    </button>
  </SimpleTooltip>
);

/**
 * The chat's header: Sabine's face, her name and what she is for.
 * `contextBadge` names what she is looking at ("Analyzing Jane Doe") in the
 * subtitle's place. The fullscreen and close controls show only when the pane
 * that holds the chat offers them (`useCoachPane`). `ink` is the header as it
 * sits in the navy top bar above her column: the bar's height, white text and
 * the bar's ghost controls.
 */
export default function AIChatHeader({
  ink = false,
  contextBadge,
  onReset,
  resetDisabled,
  onShare,
  isSharing,
  shareDisabled,
  isFullscreen,
  onFullscreenChange,
  onClose,
}) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-3 px-4",
        ink ? "h-14" : "h-16",
      )}
    >
      <SabineLead />
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate text-sm font-semibold leading-tight",
            ink ? "text-white" : "text-gray-900",
          )}
        >
          {SABINE_TITLE}
        </p>
        {contextBadge ? (
          <Badge
            tone="brand"
            variant="soft"
            size="sm"
            className="mt-0.5 max-w-full truncate"
          >
            {contextBadge}
          </Badge>
        ) : (
          <p
            className={cn(
              "truncate text-xs leading-tight",
              ink ? "text-navy-300" : "text-gray-500",
            )}
          >
            {SABINE_SUBTITLE}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center">
        <HeaderButton
          ink={ink}
          label="New chat"
          onClick={onReset}
          disabled={resetDisabled}
          data-testid="ai-chat-new-chat"
        >
          <RotateCcw className="h-5 w-5" />
        </HeaderButton>
        {onShare && (
          <HeaderButton
            ink={ink}
            label="Share chat"
            onClick={onShare}
            disabled={shareDisabled}
          >
            {isSharing ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Share2 className="h-5 w-5" />
            )}
          </HeaderButton>
        )}
        {onFullscreenChange && (
          <HeaderButton
            ink={ink}
            label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            onClick={() => onFullscreenChange(!isFullscreen)}
          >
            {isFullscreen ? (
              <Minimize2 className="h-5 w-5" />
            ) : (
              <Maximize2 className="h-5 w-5" />
            )}
          </HeaderButton>
        )}
        {onClose && (
          <HeaderButton ink={ink} label="Close chat" onClick={onClose}>
            <X className="h-5 w-5" />
          </HeaderButton>
        )}
      </div>
    </div>
  );
}
